import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { BookOpen, CalendarDays, GitMerge, Loader2, Pencil, Plus, Search, Tags, Trash2 } from 'lucide-react';
import { Button } from '@components/Button';
import { ConfirmDialog, Dialog } from '@components/Dialog';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { DateInputBR } from '@components/DateInputBR';
import { ActionButton } from '@components/Table';
import { usePermissions } from '@hooks/usePermissions';
import mediaService from '@services/mediaService';
import type { MediaEvent, MediaTag, MediaTagCategory } from '@/types/media';
import { formatDateBR } from '@/utils/formatters';
import { DocContent } from '@components/help/DocContent';

const FIELD_LABELS: Record<string, string> = {
  name: 'Nome',
  slug: 'Identificador (slug)',
  sort_order: 'Ordem',
  category_id: 'Categoria',
  synonyms: 'Sinônimos',
  is_active: 'Ativa',
  into_tag_id: 'Tag de destino',
  location: 'Local',
  event_date: 'Data',
  description: 'Descrição',
};

/** "nude, Savana ,, rose" → ["nude", "Savana", "rose"] (sem repetição, ignorando caixa). */
const parseSynonyms = (text: string): string[] => {
  const out: string[] = [];
  text
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((s) => {
      if (!out.some((o) => o.toLowerCase() === s.toLowerCase())) out.push(s);
    });
  return out;
};

type Tab = 'tags' | 'events' | 'help';

/** Taxonomia da biblioteca: categorias e tags (com sinônimos e mescla) e eventos. */
export const MediaTaxonomyView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: Tab = tabParam === 'events' || tabParam === 'help' ? tabParam : 'tags';
  const { checkPermission } = usePermissions();
  const canManage = checkPermission('media.taxonomy.manage').hasPermission;
  const [error, setError] = useState<unknown>(null);

  const tabClass = (active: boolean) =>
    clsx(
      'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors',
      active ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700',
    );

  return (
    <div className="space-y-4">
      <h1 className="text-h3 flex items-center gap-2">
        <Tags size={20} className="text-slate-500" /> Eventos e tags
      </h1>
      <div className="border-b border-slate-200 flex">
        <button type="button" className={tabClass(tab === 'tags')} onClick={() => setSearchParams({}, { replace: true })}>
          <Tags size={16} /> Tags
        </button>
        <button type="button" className={tabClass(tab === 'events')} onClick={() => setSearchParams({ tab: 'events' }, { replace: true })}>
          <CalendarDays size={16} /> Eventos
        </button>
        <button
          type="button"
          className={clsx(tabClass(tab === 'help'), 'ml-auto')}
          onClick={() => setSearchParams({ tab: 'help' }, { replace: true })}
        >
          <BookOpen size={16} /> Como funciona
        </button>
      </div>
      {tab === 'tags' && <TagsTab canManage={canManage} onError={setError} />}
      {tab === 'events' && <EventsTab canManage={canManage} onError={setError} />}
      {tab === 'help' && <DocContent rota="/media/taxonomy" />}
      <ApiErrorModal error={error} onClose={() => setError(null)} fieldLabels={FIELD_LABELS} />
    </div>
  );
};

// ------------------------------------------------------------------ Tags

interface TabProps {
  canManage: boolean;
  onError: (err: unknown) => void;
}

const TagsTab: React.FC<TabProps> = ({ canManage, onError }) => {
  const [categories, setCategories] = useState<MediaTagCategory[]>([]);
  const [tags, setTags] = useState<MediaTag[]>([]);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingTag, setEditingTag] = useState<MediaTag | 'new' | null>(null);
  const [mergingTag, setMergingTag] = useState<MediaTag | null>(null);
  const [editingCategory, setEditingCategory] = useState<MediaTagCategory | 'new' | null>(null);

  const loadCategories = useCallback(() => {
    mediaService.tagCategories().then(setCategories).catch(onError);
  }, [onError]);

  const loadTags = useCallback(async () => {
    setLoading(true);
    try {
      setTags(await mediaService.tags({ includeInactive }));
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  }, [includeInactive, onError]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'pt-BR')),
    [categories],
  );

  const visibleTags = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tags
      .filter((t) => categoryId === null || t.category_id === categoryId)
      .filter(
        (t) => !q || t.name.toLowerCase().includes(q) || t.synonyms.some((s) => s.toLowerCase().includes(q)),
      )
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }, [tags, categoryId, search]);

  const reloadAll = () => {
    loadTags();
    loadCategories();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[16rem_1fr] gap-4">
      {/* Categorias */}
      <div className="card p-3 h-fit">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-700">Categorias</h3>
          {canManage && (
            <button
              type="button"
              onClick={() => setEditingCategory('new')}
              className="p-1 rounded text-emerald-700 hover:bg-emerald-50"
              title="Nova categoria"
            >
              <Plus size={16} />
            </button>
          )}
        </div>
        <ul className="space-y-0.5">
          <li>
            <button
              type="button"
              onClick={() => setCategoryId(null)}
              className={clsx(
                'w-full text-left px-2 py-1.5 rounded-md text-sm flex justify-between',
                categoryId === null ? 'bg-emerald-50 text-emerald-800 font-medium' : 'text-slate-700 hover:bg-slate-50',
              )}
            >
              Todas <span className="text-slate-400 tabular-nums">{tags.length}</span>
            </button>
          </li>
          {sortedCategories.map((c) => (
            <li key={c.id} className="group flex items-center">
              <button
                type="button"
                onClick={() => setCategoryId(c.id)}
                className={clsx(
                  'flex-1 text-left px-2 py-1.5 rounded-md text-sm flex justify-between',
                  categoryId === c.id ? 'bg-emerald-50 text-emerald-800 font-medium' : 'text-slate-700 hover:bg-slate-50',
                  !c.is_active && 'opacity-50',
                )}
              >
                {c.name}
                <span className="text-slate-400 tabular-nums">{c.tag_count}</span>
              </button>
              {canManage && (
                <button
                  type="button"
                  onClick={() => setEditingCategory(c)}
                  className="p-1 rounded text-slate-400 hover:text-emerald-700 opacity-0 group-hover:opacity-100"
                  title="Editar categoria"
                >
                  <Pencil size={12} />
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Tags */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[14rem]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="input-field pl-9"
              placeholder="Buscar tag ou sinônimo…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox-field"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Mostrar inativas
          </label>
          {canManage && (
            <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setEditingTag('new')} disabled={categories.length === 0}>
              Nova tag
            </Button>
          )}
        </div>

        <div className="table-container bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="table-header">
                <tr>
                  <th>Tag</th>
                  {categoryId === null && <th>Categoria</th>}
                  <th>Sinônimos</th>
                  <th className="text-right">Uso</th>
                  <th>Situação</th>
                  {canManage && <th className="w-24"></th>}
                </tr>
              </thead>
              <tbody>
                {loading && tags.length === 0 && (
                  <tr>
                    <td colSpan={6} className="table-cell text-center text-slate-400">
                      <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                    </td>
                  </tr>
                )}
                {!loading && visibleTags.length === 0 && (
                  <tr>
                    <td colSpan={6} className="table-cell text-center py-10 text-slate-500">
                      Nenhuma tag encontrada.
                    </td>
                  </tr>
                )}
                {visibleTags.map((t) => (
                  <tr key={t.id} className={clsx('table-row', !t.is_active && 'opacity-60')}>
                    <td className="table-cell font-medium text-slate-800">{t.name}</td>
                    {categoryId === null && <td className="table-cell">{t.category_name}</td>}
                    <td className="table-cell">
                      <div className="flex flex-wrap gap-1">
                        {t.synonyms.length === 0 && <span className="text-slate-300">—</span>}
                        {t.synonyms.map((s) => (
                          <span key={s} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                            {s}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="table-cell text-right tabular-nums">{t.usage_count.toLocaleString('pt-BR')}</td>
                    <td className="table-cell">
                      {t.is_active ? <span className="badge-success">Ativa</span> : <span className="badge-neutral">Inativa</span>}
                    </td>
                    {canManage && (
                      <td className="table-cell">
                        <div className="flex items-center gap-1 justify-end">
                          <ActionButton icon={<Pencil size={16} />} variant="edit" tooltip="Editar" onClick={() => setEditingTag(t)} />
                          {t.is_active && (
                            <ActionButton icon={<GitMerge size={16} />} tooltip="Mesclar em outra tag" onClick={() => setMergingTag(t)} />
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editingTag && (
        <TagDialog
          tag={editingTag === 'new' ? null : editingTag}
          categories={sortedCategories}
          defaultCategoryId={categoryId}
          onClose={() => setEditingTag(null)}
          onSaved={() => {
            setEditingTag(null);
            reloadAll();
          }}
          onError={onError}
        />
      )}
      {mergingTag && (
        <MergeDialog
          tag={mergingTag}
          tags={tags.filter((t) => t.is_active && t.id !== mergingTag.id)}
          onClose={() => setMergingTag(null)}
          onMerged={() => {
            setMergingTag(null);
            reloadAll();
          }}
          onError={onError}
        />
      )}
      {editingCategory && (
        <CategoryDialog
          category={editingCategory === 'new' ? null : editingCategory}
          onClose={() => setEditingCategory(null)}
          onSaved={() => {
            setEditingCategory(null);
            loadCategories();
          }}
          onError={onError}
        />
      )}
    </div>
  );
};

const DialogFooter: React.FC<{ onCancel: () => void; onConfirm: () => void; saving: boolean; disabled?: boolean; confirmText?: string }> = ({
  onCancel,
  onConfirm,
  saving,
  disabled,
  confirmText = 'Salvar',
}) => (
  <div className="flex justify-end gap-3">
    <Button variant="outline" onClick={onCancel} disabled={saving}>
      Cancelar
    </Button>
    <Button variant="primary" onClick={onConfirm} loading={saving} disabled={disabled}>
      {confirmText}
    </Button>
  </div>
);

const TagDialog: React.FC<{
  tag: MediaTag | null;
  categories: MediaTagCategory[];
  defaultCategoryId: number | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (err: unknown) => void;
}> = ({ tag, categories, defaultCategoryId, onClose, onSaved, onError }) => {
  const [name, setName] = useState(tag?.name ?? '');
  const [categoryId, setCategoryId] = useState(String(tag?.category_id ?? defaultCategoryId ?? categories.find((c) => c.is_active)?.id ?? ''));
  const [synonyms, setSynonyms] = useState((tag?.synonyms ?? []).join(', '));
  const [isActive, setIsActive] = useState(tag?.is_active ?? true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (tag) await mediaService.updateTag(tag.id, { name: name.trim(), synonyms: parseSynonyms(synonyms), is_active: isActive });
      else await mediaService.createTag({ category_id: Number(categoryId), name: name.trim(), synonyms: parseSynonyms(synonyms) });
      onSaved();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={tag ? 'Editar tag' : 'Nova tag'}
      footer={<DialogFooter onCancel={onClose} onConfirm={save} saving={saving} disabled={!name.trim() || !categoryId} />}
    >
      <div className="space-y-3">
        <div>
          <label className="text-label block mb-1">Categoria</label>
          <select className="select-field" value={categoryId} disabled={!!tag} onChange={(e) => setCategoryId(e.target.value)}>
            {categories
              .filter((c) => c.is_active || String(c.id) === categoryId)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </div>
        <div>
          <label className="text-label block mb-1">Nome</label>
          <input className="input-field" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
        <div>
          <label className="text-label block mb-1">Sinônimos</label>
          <textarea
            className="textarea-field"
            rows={2}
            value={synonyms}
            onChange={(e) => setSynonyms(e.target.value)}
            placeholder="Separe por vírgula. Ex.: Savana, nude"
          />
          <p className="text-small mt-1">A busca por qualquer sinônimo encontra as mídias desta tag.</p>
        </div>
        {tag && (
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" className="checkbox-field" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Ativa
          </label>
        )}
      </div>
    </Dialog>
  );
};

const MergeDialog: React.FC<{
  tag: MediaTag;
  tags: MediaTag[];
  onClose: () => void;
  onMerged: () => void;
  onError: (err: unknown) => void;
}> = ({ tag, tags, onClose, onMerged, onError }) => {
  const [target, setTarget] = useState('');
  const [saving, setSaving] = useState(false);

  const groups = useMemo(() => {
    const map = new Map<string, MediaTag[]>();
    [...tags]
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
      .forEach((t) => map.set(t.category_name, [...(map.get(t.category_name) ?? []), t]));
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'));
  }, [tags]);

  const merge = async () => {
    setSaving(true);
    try {
      await mediaService.mergeTag(tag.id, Number(target));
      onMerged();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title="Mesclar tag"
      footer={<DialogFooter onCancel={onClose} onConfirm={merge} saving={saving} disabled={!target} confirmText="Mesclar" />}
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          Os vínculos de <strong>{tag.name}</strong> ({tag.category_name}, {tag.usage_count} uso(s)) passam para a tag escolhida e{' '}
          <strong>{tag.name}</strong> fica inativa. Dica: depois, cadastre “{tag.name}” como sinônimo da tag de destino.
        </p>
        <div>
          <label className="text-label block mb-1">Mesclar em</label>
          <select className="select-field" value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">Escolha a tag de destino…</option>
            {groups.map(([cat, list]) => (
              <optgroup key={cat} label={cat}>
                {list.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      </div>
    </Dialog>
  );
};

const CategoryDialog: React.FC<{
  category: MediaTagCategory | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (err: unknown) => void;
}> = ({ category, onClose, onSaved, onError }) => {
  const [name, setName] = useState(category?.name ?? '');
  const [slug, setSlug] = useState(category?.slug ?? '');
  const [sortOrder, setSortOrder] = useState(String(category?.sort_order ?? 0));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const data = { name: name.trim(), slug: slug.trim() || null, sort_order: Number(sortOrder) || 0 };
    try {
      if (category) await mediaService.updateTagCategory(category.id, data);
      else await mediaService.createTagCategory(data);
      onSaved();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={category ? 'Editar categoria' : 'Nova categoria'}
      size="sm"
      footer={<DialogFooter onCancel={onClose} onConfirm={save} saving={saving} disabled={!name.trim()} />}
    >
      <div className="space-y-3">
        <div>
          <label className="text-label block mb-1">Nome</label>
          <input className="input-field" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-label block mb-1">Identificador (slug)</label>
            <input className="input-field" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="automático" />
          </div>
          <div>
            <label className="text-label block mb-1">Ordem</label>
            <input className="input-field" type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
          </div>
        </div>
      </div>
    </Dialog>
  );
};

// ------------------------------------------------------------------ Eventos

const EventsTab: React.FC<TabProps> = ({ canManage, onError }) => {
  const [events, setEvents] = useState<MediaEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<MediaEvent | 'new' | null>(null);
  const [deleting, setDeleting] = useState<MediaEvent | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEvents(await mediaService.events());
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = events
    .filter((e) => showInactive || e.is_active)
    .sort((a, b) => (b.event_date ?? '').localeCompare(a.event_date ?? '') || a.name.localeCompare(b.name, 'pt-BR'));

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await mediaService.deleteEvent(deleting.id);
      setDeleting(null);
      load();
    } catch (err) {
      setDeleting(null);
      onError(err);
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" className="checkbox-field" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Mostrar inativos
        </label>
        {canManage && (
          <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setEditing('new')}>
            Novo evento
          </Button>
        )}
      </div>
      <div className="table-container bg-white">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th>Evento</th>
                <th>Local</th>
                <th>Data</th>
                <th className="text-right">Mídias</th>
                <th>Situação</th>
                {canManage && <th className="w-24"></th>}
              </tr>
            </thead>
            <tbody>
              {loading && events.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center text-slate-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                  </td>
                </tr>
              )}
              {!loading && visible.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center py-10 text-slate-500">
                    Nenhum evento cadastrado.
                  </td>
                </tr>
              )}
              {visible.map((ev) => (
                <tr key={ev.id} className={clsx('table-row', !ev.is_active && 'opacity-60')}>
                  <td className="table-cell">
                    <p className="font-medium text-slate-800">{ev.name}</p>
                    {ev.description && <p className="text-small line-clamp-2">{ev.description}</p>}
                  </td>
                  <td className="table-cell">{ev.location || <span className="text-slate-300">—</span>}</td>
                  <td className="table-cell whitespace-nowrap">{formatDateBR(ev.event_date) || <span className="text-slate-300">—</span>}</td>
                  <td className="table-cell text-right tabular-nums">{ev.media_count.toLocaleString('pt-BR')}</td>
                  <td className="table-cell">
                    {ev.is_active ? <span className="badge-success">Ativo</span> : <span className="badge-neutral">Inativo</span>}
                  </td>
                  {canManage && (
                    <td className="table-cell">
                      <div className="flex items-center gap-1 justify-end">
                        <ActionButton icon={<Pencil size={16} />} variant="edit" tooltip="Editar" onClick={() => setEditing(ev)} />
                        {ev.is_active && (
                          <ActionButton icon={<Trash2 size={16} />} variant="delete" tooltip="Excluir" onClick={() => setDeleting(ev)} />
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EventDialog
          event={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
          onError={onError}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Excluir evento"
        message={`Excluir "${deleting?.name ?? ''}"? As ${deleting?.media_count ?? 0} mídia(s) vinculadas ficam sem evento.`}
        confirmText="Excluir"
        severity="danger"
        loading={deleteBusy}
      />
    </div>
  );
};

const EventDialog: React.FC<{
  event: MediaEvent | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (err: unknown) => void;
}> = ({ event, onClose, onSaved, onError }) => {
  const [name, setName] = useState(event?.name ?? '');
  const [location, setLocation] = useState(event?.location ?? '');
  const [eventDate, setEventDate] = useState(event?.event_date ?? '');
  const [description, setDescription] = useState(event?.description ?? '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const data = {
      name: name.trim(),
      location: location.trim() || null,
      event_date: eventDate || null,
      description: description.trim() || null,
    };
    try {
      if (event) await mediaService.updateEvent(event.id, data);
      else await mediaService.createEvent(data);
      onSaved();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={event ? 'Editar evento' : 'Novo evento'}
      footer={<DialogFooter onCancel={onClose} onConfirm={save} saving={saving} disabled={!name.trim()} />}
    >
      <div className="space-y-3">
        <div>
          <label className="text-label block mb-1">Nome</label>
          <input className="input-field" value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="Ex.: Casa Cor 2026" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-label block mb-1">Local</label>
            <input className="input-field" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ex.: Palácio Tangará" />
          </div>
          <div>
            <label className="text-label block mb-1">Data</label>
            <DateInputBR className="input-field w-full" value={eventDate} onChange={setEventDate} />
          </div>
        </div>
        <div>
          <label className="text-label block mb-1">Descrição</label>
          <textarea className="textarea-field" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
      </div>
    </Dialog>
  );
};
